/**
 * Hook to manage a GQL query.
 *
 * For details on usage, see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/1958347015/useQuery.
 */
import {
    FetchPolicy,
    toGqlOperation,
    useCachedEffect,
    type Result,
} from "@khanacademy/wonder-blocks-data";
import {documentNode, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import {DefaultQueryScope} from "./constants.ts";
import type {GqlQueryOptions} from "./types.ts";
import {useGql} from "./use-gql.ts";
import {useRequestIdBuilder} from "./use-request-id-builder.ts";

/**
 * Hook to fetch and cache a query.
 *
 * A hook that fetches a query from GraphQL and stores the result in the shared
 * (client-side) cache. See also: useFastlyQuery.
 *
 * If the operation, module, targetLocale, curriculum, or variables change,
 * the query will be re-fetched (unless `skip` is true). However, if the
 * `NetworkOnly` or `CacheAndNetwork` fetch policies are used, and an earlier
 * fetch already happened, then the old network value will still be returned.
 * This is a known issue (see https://khanacademy.atlassian.net/browse/WB-2085).
 * If you need to do this, you can force an update by calling the returned
 * refetch function explicitly. Note that changing the fetch policy does not
 * cause this cached network result to clear.
 *
 * In addition, though different calls to useQuery for the same request will
 * share the same cache and associated inflight requests, direct cache updates
 * only trigger re-renders or `cacheUpdated` calls for the specific hook
 * instances that caused the update. This means that if you have multiple
 * components using useQuery for the same request, and one explicitly refetches
 * causing an update to the cache, the other components will not re-render or
 * have their `cacheUpdated` called. Since hooks share inflight requests, the
 * regular usage is not impacted by this - it is specific to explicit refetches,
 * but it is something to be aware of.
 *
 * @params operation - The GraphQL operation to execute.
 * @params options - Options for the query, including:
 *   - curriculum, module, targetLocale: Context for the request.
 *   - variables: The variables for the query.
 *   - fetchPolicy: The fetch policy to use (defaults to CacheBeforeNetwork).
 *   - retainResultOnChange: Whether to retain the existing result when the
 *     request ID changes (defaults to false).
 *   - skip: If true, the query will not be executed (defaults to false).
 *   - cacheUpdated: An optional callback that is invoked when the cached
 *     result is updated. This disables re-rendering of the component when
 *     the cache is updated, allowing you to determine how re-renders are
 *     triggered after cache updates.
 */
export const useQuery = <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(
    operation: GqlOperation<TData, TVariables>,
    {
        curriculum,
        module,
        retainResultOnChange = false,
        skip = false,
        targetLocale,
        variables,
        fetchPolicy = FetchPolicy.CacheBeforeNetwork,
        cacheUpdated,
    }: GqlQueryOptions<TData, TVariables> = Object.freeze({}),
): [Result<TData>, () => void] => {
    if (process.env.NODE_ENV !== "production") {
        const wbdOperation = toGqlOperation(documentNode(operation));
        if (wbdOperation.type !== "query") {
            throw new KAError(
                "Mutations are not valid for useQuery",
                Errors.InvalidInput,
            );
        }
    }

    // Normally, we rely on the gqlFetch to handle combining our partial
    // context with the defaults specified with GqlRouter. However, in this
    // case we need that info here to be able to get a request ID.
    const requestIdBuilder = useRequestIdBuilder();
    const requestId = requestIdBuilder(operation, variables, {
        module,
        targetLocale,
        curriculum,
    });

    // Now we perform the actual querying.
    const gqlFetch = useGql();
    const result = useCachedEffect<TData>(
        requestId,
        () =>
            gqlFetch(operation, {
                variables,
                context: {
                    curriculum,
                    module,
                    targetLocale,
                },
            }),
        {
            onResultChanged: cacheUpdated,
            retainResultOnChange,
            skip,
            scope: DefaultQueryScope,
            fetchPolicy,
        },
    );
    return result;
};
